import React from 'react';
import {useDispatch} from 'react-redux';
import {WeatherService} from '../api';
import {AqForecastResponse, Location} from '../api/types';
import {
  EMPTY_AQ_FORECAST,
  getLocationAqForecastData,
  pushLocationAqForecast,
} from '../store';
import {Dates} from '../utils';
import useApiClient from './useApiClient';
import useParamSelector from './useParamSelector';

const STALENESS_MIN = 30;
const RETRY_COOLDOWN_MIN = 5;

const useLocationAqForecast = (location: Location) => {
  const {fetch} = useApiClient();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = React.useState(false);
  // Keyed by location hash: the dashboard swaps the location on the same
  // mounted component (the selection lives in redux), so a single shared
  // timestamp would let the previous location's cooldown swallow the new
  // location's very first fetch and leave the card hidden until the app is
  // restarted.
  const lastAttemptAtRef = React.useRef<Record<string, number>>({});

  const locationHash = location?.hash;
  const locationLat = location?.coordinates?.latitude;
  const locationLon = location?.coordinates?.longitude;
  const locationAlt = location?.coordinates?.altitude;
  const locationTimezone = location?.timezone;

  const aqForecastData: AqForecastResponse = useParamSelector(
    getLocationAqForecastData,
    locationHash,
  );

  const isUpdateRequired = React.useCallback(() => {
    const isStale =
      !aqForecastData ||
      aqForecastData === EMPTY_AQ_FORECAST ||
      !aqForecastData.computed_at ||
      Dates.timeDiffUtcWithNowInMin(aqForecastData.computed_at) > STALENESS_MIN;
    if (!isStale) {
      return false;
    }
    const lastAttemptAt = lastAttemptAtRef.current[locationHash];
    if (
      lastAttemptAt !== undefined &&
      Dates.timeDiffUtcWithNowInMin(lastAttemptAt) < RETRY_COOLDOWN_MIN
    ) {
      return false;
    }
    return true;
  }, [aqForecastData, locationHash]);

  const updateAqForecast = React.useCallback(async () => {
    // Use a finite check (not falsy) so a valid 0 coordinate — equator
    // latitude or prime-meridian longitude — is not treated as missing. See
    // MR !73.
    if (
      isLoading ||
      !locationHash ||
      !Number.isFinite(locationLat) ||
      !Number.isFinite(locationLon)
    ) {
      return;
    }

    lastAttemptAtRef.current[locationHash] = Dates.now();
    setIsLoading(true);
    try {
      const data: AqForecastResponse = await fetch(
        WeatherService.locationAqForecast({
          lat: locationLat,
          lon: locationLon,
          elevation: locationAlt,
          timezone: locationTimezone,
        }),
        {ignore404: true},
      );

      dispatch(pushLocationAqForecast({locationHash, data}));
    } catch (_error) {
      // Cooldown via lastAttemptAtRef prevents a hot retry loop on persistent failure.
    } finally {
      setIsLoading(false);
    }
  }, [
    fetch,
    locationHash,
    locationLat,
    locationLon,
    locationAlt,
    locationTimezone,
    dispatch,
    isLoading,
  ]);

  const memoizedReturn = React.useMemo(
    () => ({
      aqForecastData,
      isLoading,
      isUpdateRequired,
      updateAqForecast,
    }),
    [aqForecastData, isLoading, isUpdateRequired, updateAqForecast],
  );

  return memoizedReturn;
};

export default useLocationAqForecast;
